import { useEffect, useState } from "react";
import { Ksef, Finance } from "../lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Wand2, ArrowDownToLine } from "lucide-react";
import { useConfirm } from "./ConfirmProvider";
import { PurchaseInvoiceRow } from "./PurchaseInvoiceRow";
import { useDeepLink, flashRecord } from "../lib/deepLink";

export default function PurchaseInvoices() {
  const [purchases, setPurchases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState("all");
  const [lastResult, setLastResult] = useState(null);
  const { confirm } = useConfirm();

  const refresh = async () => {
    try {
      const [rows, options] = await Promise.all([Ksef.listPurchases(), Ksef.purchaseCategories()]);
      setPurchases(rows); setCategories(options); setError("");
    } catch (e) { setError(e.response?.data?.detail || "Nie udało się pobrać faktur."); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  useDeepLink("focus", !loading, (id) => { setFilter("all"); setTimeout(() => flashRecord(id), 50); });

  const run = async (key, action, message) => {
    setBusy(key);
    try { const result = await action(); toast.success(message); await refresh(); return result; }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(""); }
  };
  const sync = async () => {
    const result = await run("sync", () => Ksef.syncPurchases(days), "Synchronizacja zakończona");
    if (result) setLastResult({ imported: result.auto_imported, review: result.to_review, downloaded: result.downloaded, errors: result.errors });
  };
  const classify = async () => {
    const result = await run("classify", Ksef.classifyPurchases, "Przypisywanie kategorii zakończone");
    if (result) setLastResult(result);
  };
  const togglePayment = async (p) => {
    const paid = p.payment_status !== "paid";
    if (!(await confirm(`${paid ? "Oznaczyć jako opłaconą" : "Cofnąć oznaczenie płatności"}: ${p.invoice_number || p.ksef_number}?`))) return;
    await run(p.ksef_number, () => Finance.setPayment("ksef", p.ksef_number, paid), "Zapisano status płatności");
  };
  const remove = async (p) => {
    if (!(await confirm(`Usunąć lokalną kopię ${p.invoice_number || p.ksef_number}? Oryginał zostaje w KSeF.`, { tone: "danger", confirmLabel: "Usuń" }))) return;
    await run(p.ksef_number, () => Ksef.deletePurchase(p.ksef_number), "Usunięto lokalną kopię");
  };
  const visible = purchases.filter((p) => filter === "all" ||
    (filter === "review" && !p.imported_to_kup && p.classification?.status === "review") ||
    (filter === "unpaid" && p.payment_status !== "paid") ||
    (filter === "paid" && p.payment_status === "paid") ||
    (filter === "imported" && p.imported_to_kup));

  if (loading) return <div data-testid="purchases-loading" className="flex items-center gap-2 p-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" />Ładuję faktury zakupu…</div>;
  return <div className="space-y-4 min-w-0" data-testid="purchase-invoices">
    <div className="flex flex-wrap items-center gap-3 border-b-2 border-zinc-900 pb-4">
      <label className="text-xs font-mono">Zakres
        <select data-testid="purchase-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={!!busy} className="ml-2 border border-zinc-300 px-2 py-2">
          {[7, 30, 60, 90, 180, 365].map((n) => <option key={n} value={n}>{`${n} dni`}</option>)}
        </select>
      </label>
      <button data-testid="btn-sync-purchases" disabled={!!busy} onClick={sync} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold bg-brand text-white hover:bg-brand-hover transition-colors disabled:opacity-50">
        {busy === "sync" ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}Pobierz z KSeF
      </button>
      <button data-testid="btn-classify-purchases" disabled={!!busy || !purchases.length} onClick={classify} className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold border border-zinc-900 hover:bg-zinc-100 transition-colors disabled:opacity-50">
        {busy === "classify" ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}Przypisz do KUP
      </button>
      <select aria-label="Filtr faktur" data-testid="purchase-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-zinc-300 px-2 py-2 text-sm sm:ml-auto">
        <option value="all">Wszystkie</option><option value="review">Do sprawdzenia</option><option value="unpaid">Nieopłacone</option><option value="paid">Opłacone</option><option value="imported">W KUP</option>
      </select>
    </div>
    {error && <div role="alert" data-testid="purchases-error" className="bg-red-50 text-red-800 p-3 text-sm">{error} <button data-testid="purchases-retry" onClick={refresh} className="underline font-bold">Ponów</button></div>}
    {lastResult && <div data-testid="purchase-classify-result" className="text-sm bg-emerald-50 border-l-4 border-emerald-600 p-3">
      Dodano do KUP: <b>{lastResult.imported || 0}</b> · Do sprawdzenia: <b>{lastResult.review || 0}</b>
      {lastResult.downloaded != null && <span> · Pobrano: {lastResult.downloaded}</span>}
      {!!lastResult.errors?.length && <div data-testid="purchase-sync-errors" role="alert" className="text-red-700 mt-2 break-words">{lastResult.errors.join(" · ")}</div>}
    </div>}
    <div className="flex items-center gap-2"><ArrowDownToLine size={16} /><h2 className="font-heading font-bold text-base md:text-lg">Faktury zakupu</h2><span data-testid="purchases-count" className="text-xs font-mono text-zinc-500">{visible.length} / {purchases.length}</span></div>
    <p data-testid="purchase-tax-notice" className="text-xs text-zinc-500">Kategoria kosztu nie potwierdza prawa do odliczenia VAT ani zaliczenia wydatku do kosztów podatkowych.</p>
    <div className="divide-y divide-zinc-200 border-y border-zinc-200">
      {!visible.length && <div data-testid="purchases-empty" className="py-10 text-center text-sm text-zinc-500">Brak faktur w tym widoku.</div>}
      {visible.map((p) => <div key={p.ksef_number} data-record-id={p.ksef_number}><PurchaseInvoiceRow p={p} categories={categories} busy={!!busy}
        onImport={(category) => run(p.ksef_number, () => Ksef.importPurchaseToKup(p.ksef_number, category), "Dodano do KUP — koszt planowany")}
        onCategory={(category) => run(p.ksef_number, () => Ksef.setPurchaseCategory(p.ksef_number, category), "Zapisano kategorię")}
        onDueDate={(due) => run(p.ksef_number, () => Ksef.setPurchaseDueDate(p.ksef_number, due), "Zapisano termin płatności")}
        onPayment={() => togglePayment(p)} onDelete={() => remove(p)} /></div>)}
    </div>
  </div>;
}